import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { apiFetch, apiFetchBlob } from "../api/api";
import { logClientError } from "../api/errorLogger";
import ConfirmModal from "../components/ConfirmModal";
import TenantFilesModal from "../components/TenantFilesModal";
import TenantCreationFiles from "../components/TenantCreationFiles";
import TenantFilterModal, { TENANT_MAILBOX_FILTERS } from "../components/TenantFilterModal";
import TenantProfileModal from "../components/TenantProfileModal";
import TenantTable from "../components/TenantTable";
import { TENANT_FIELD_LABELS, changedTenantProfileFields, tenantDisplayName } from "../utils/tenantProfile";

export default function Tenants() {
  const location = useLocation();
  const [tenants, setTenants] = useState([]);
  const [mailboxes, setMailboxes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [pageError, setPageError] = useState("");
  const [success, setSuccess] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [mailboxFilter, setMailboxFilter] = useState("all");
  const [filterOpen, setFilterOpen] = useState(false);

  const [profileOpen, setProfileOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [profileError, setProfileError] = useState("");
  const [profileFieldErrors, setProfileFieldErrors] = useState({});
  const [pendingUpdate, setPendingUpdate] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [creationFiles, setCreationFiles] = useState([]);
  const [creationFileError, setCreationFileError] = useState("");
  const [createdTenant, setCreatedTenant] = useState(null);
  const creationFileId = useRef(0);
  const creationBusy = useRef(false);

  const [filesTenant, setFilesTenant] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [filesWorking, setFilesWorking] = useState(false);
  const [fileError, setFileError] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileInputVersion, setFileInputVersion] = useState(0);

  const fetchTenants = useCallback(async () => {
    const data = await apiFetch("/tenants");
    setTenants(data);
  }, []);

  const fetchMailboxes = useCallback(async () => {
    const data = await apiFetch("/mailboxes");
    setMailboxes(data);
  }, []);

  const refreshTenantData = useCallback(
    () => Promise.all([fetchTenants(), fetchMailboxes()]),
    [fetchMailboxes, fetchTenants],
  );

  const loadTenants = useCallback(async () => {
    setPageError("");
    setIsLoading(true);
    try {
      await refreshTenantData();
    } catch (err) {
      setPageError(err.message);
      logClientError(err, { page: "Tenants", action: "loadTenants" });
    } finally {
      setIsLoading(false);
    }
  }, [refreshTenantData]);

  useEffect(() => {
    loadTenants();
  }, [loadTenants]);

  useEffect(() => {
    if (location.state?.action === "add") {
      setSelectedTenant(null);
      setProfileError("");
      setProfileFieldErrors({});
      setCreationFiles([]);
      setCreationFileError("");
      setCreatedTenant(null);
      setProfileOpen(true);
    }
  }, [location.state]);

  const openCreateProfile = () => {
    if (creationBusy.current) return;
    setCreationFiles([]);
    setCreationFileError("");
    setCreatedTenant(null);
    setSelectedTenant(null);
    setProfileError("");
    setProfileFieldErrors({});
    setSuccess("");
    setProfileOpen(true);
  };

  const openTenantProfile = (tenant) => {
    if (creationBusy.current) return;
    setSelectedTenant(tenant);
    setProfileError("");
    setProfileFieldErrors({});
    setSuccess("");
    setProfileOpen(true);
  };

  const closeProfile = () => {
    if (isSaving || creationBusy.current || pendingUpdate || confirmDelete) return;
    if (createdTenant) setSuccess("Tenant created. Uploaded files are saved; remaining files were not uploaded.");
    setCreationFiles([]);
    setCreationFileError("");
    setCreatedTenant(null);
    setProfileOpen(false);
    setSelectedTenant(null);
    setProfileError("");
    setProfileFieldErrors({});
  };

  const addCreationFiles = (files) => {
    if (creationBusy.current) return;
    const errors = [];
    const accepted = files.filter((file) => {
      if (file.type !== "application/pdf") {
        errors.push(`${file.name}: Only PDFs allowed.`);
        return false;
      }
      if (file.size > 5 * 1024 * 1024) {
        errors.push(`${file.name}: File exceeds the 5 MB limit.`);
        return false;
      }
      return true;
    }).map((file) => ({ id: ++creationFileId.current, file, status: "pending", error: "" }));
    setCreationFiles((current) => [...current, ...accepted]);
    setCreationFileError(errors.join(" "));
  };

  const uploadCreationFiles = async (tenant, entries) => {
    const results = [];
    for (const entry of entries) {
      setCreationFiles((current) => current.map((item) => item.id === entry.id
        ? { ...item, status: "uploading", error: "" } : item));
      const formData = new FormData();
      formData.append("file", entry.file);
      let result;
      try {
        await apiFetch(`/documents/tenant/${tenant.id}`, { method: "POST", body: formData });
        result = { ...entry, status: "uploaded", error: "" };
      } catch (err) {
        result = { ...entry, status: "failed", error: err.message };
        logClientError(err, { page: "Tenants", action: "uploadCreationDocument", tenantId: tenant.id });
      }
      results.push(result);
      setCreationFiles((current) => current.map((item) => item.id === entry.id ? result : item));
    }
    if (results.every((entry) => entry.status === "uploaded")) {
      setProfileOpen(false);
      setCreatedTenant(null);
      setCreationFiles([]);
      setCreationFileError("");
      setSuccess("Tenant created successfully.");
    }
    try {
      await refreshTenantData();
    } catch (err) {
      setPageError(`Tenant saved, but the tenant list could not be refreshed: ${err.message}`);
      logClientError(err, { page: "Tenants", action: "refreshAfterTenantCreation", tenantId: tenant.id });
    }
  };

  const retryCreationFiles = async () => {
    if (creationBusy.current || !createdTenant) return;
    creationBusy.current = true;
    setIsSaving(true);
    setPageError("");
    try {
      await uploadCreationFiles(createdTenant, creationFiles.filter((entry) => entry.status === "failed"));
    } finally {
      creationBusy.current = false;
      setIsSaving(false);
    }
  };

  const saveTenant = async (profile) => {
    if (creationBusy.current || createdTenant) return;
    setProfileError("");
    setProfileFieldErrors({});

    if (selectedTenant) {
      const changedFields = changedTenantProfileFields(selectedTenant, profile);
      const currentMailboxIds = (selectedTenant.mailboxes || []).map(({ id }) => id).sort((left, right) => left - right);
      const selectedMailboxIds = Array.isArray(profile.mailboxIds)
        ? [...profile.mailboxIds].sort((left, right) => left - right)
        : currentMailboxIds;
      if (currentMailboxIds.join(",") !== selectedMailboxIds.join(",")) changedFields.push("mailboxIds");
      if (!changedFields.length) return;
      setPendingUpdate({ profile, changedFields });
      return;
    }

    creationBusy.current = true;
    setIsSaving(true);
    setPageError("");
    let tenant;
    try {
      tenant = await apiFetch("/tenants", { method: "POST", body: JSON.stringify(profile) });
    } catch (err) {
      setProfileError(err.message);
      setProfileFieldErrors(err.data?.fields || {});
      logClientError(err, { page: "Tenants", action: "createTenant" });
      creationBusy.current = false;
      setIsSaving(false);
      return;
    }
    setCreatedTenant(tenant);
    try {
      await uploadCreationFiles(tenant, creationFiles);
    } finally {
      creationBusy.current = false;
      setIsSaving(false);
    }
  };

  const applyTenantUpdate = async () => {
    if (!selectedTenant || !pendingUpdate) return;
    setIsSaving(true);
    setProfileError("");
    setProfileFieldErrors({});
    try {
      await apiFetch(`/tenants/${selectedTenant.id}`, {
        method: "PATCH",
        body: JSON.stringify(pendingUpdate.profile),
      });
      setPendingUpdate(null);
      setProfileOpen(false);
      setSelectedTenant(null);
      setSuccess("Tenant profile updated successfully.");
      await refreshTenantData();
    } catch (err) {
      setPendingUpdate(null);
      setProfileError(err.message);
      setProfileFieldErrors(err.data?.fields || {});
      logClientError(err, { page: "Tenants", action: "updateTenant", tenantId: selectedTenant.id });
    } finally {
      setIsSaving(false);
    }
  };

  const deleteTenant = async () => {
    if (!selectedTenant) return;
    setIsSaving(true);
    setDeleteError("");
    try {
      const result = await apiFetch(`/tenants/${selectedTenant.id}`, { method: "DELETE" });
      const released = result.releasedMailboxes || [];
      setSuccess(`${result.message}${released.length ? ` Released mailbox(es): ${released.join(", ")}.` : ""}`);
      setConfirmDelete(false);
      setProfileOpen(false);
      setSelectedTenant(null);
      await refreshTenantData();
    } catch (err) {
      const mailboxes = err.data?.mailboxes;
      setDeleteError(`${err.message}${mailboxes?.length ? ` Mailbox(es): ${mailboxes.join(", ")}.` : ""}`);
      logClientError(err, { page: "Tenants", action: "deleteTenant", tenantId: selectedTenant.id });
    } finally {
      setIsSaving(false);
    }
  };

  const fetchTenantDocuments = useCallback(async (tenantId) => {
    const data = await apiFetch(`/documents?tenantId=${tenantId}`);
    setDocuments(data);
  }, []);

  const openFiles = async (tenant) => {
    setFilesTenant(tenant);
    setDocuments([]);
    setSelectedFile(null);
    setFileError("");
    setFilesLoading(true);
    try {
      await fetchTenantDocuments(tenant.id);
    } catch (err) {
      setFileError(err.message);
      logClientError(err, { page: "Tenants", action: "loadDocuments", tenantId: tenant.id });
    } finally {
      setFilesLoading(false);
    }
  };

  const closeFiles = () => {
    if (filesWorking) return;
    setFilesTenant(null);
    setDocuments([]);
    setSelectedFile(null);
    setFileError("");
  };

  const uploadDocument = async () => {
    if (!filesTenant || !selectedFile) return;
    setFileError("");
    setFilesWorking(true);
    const formData = new FormData();
    formData.append("file", selectedFile);
    try {
      await apiFetch(`/documents/tenant/${filesTenant.id}`, { method: "POST", body: formData });
      setSelectedFile(null);
      setFileInputVersion((version) => version + 1);
      await Promise.all([fetchTenantDocuments(filesTenant.id), fetchTenants()]);
    } catch (err) {
      setFileError(err.message);
      logClientError(err, { page: "Tenants", action: "uploadDocument", tenantId: filesTenant.id });
    } finally {
      setFilesWorking(false);
    }
  };

  const deleteDocument = async (docId) => {
    if (!filesTenant) return;
    setFileError("");
    setFilesWorking(true);
    try {
      await apiFetch(`/documents/${docId}`, { method: "DELETE" });
      await Promise.all([fetchTenantDocuments(filesTenant.id), fetchTenants()]);
    } catch (err) {
      setFileError(err.message);
      logClientError(err, { page: "Tenants", action: "deleteDocument", tenantId: filesTenant.id, docId });
    } finally {
      setFilesWorking(false);
    }
  };

  const openDocument = async (docId) => {
    setFileError("");
    let previewWindow;
    try {
      previewWindow = window.open("about:blank", "_blank");
      if (!previewWindow) throw new Error("Allow pop-ups to open this document");
      previewWindow.opener = null;
      const blob = await apiFetchBlob(`/documents/${docId}/download`);
      const objectUrl = URL.createObjectURL(blob);
      previewWindow.location.replace(objectUrl);
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    } catch (err) {
      previewWindow?.close();
      setFileError(err.message);
      logClientError(err, { page: "Tenants", action: "openDocument", docId });
    }
  };

  const mailboxNumbers = selectedTenant?.mailboxes?.map((mailbox) => mailbox.number) || [];
  const filterLabel = TENANT_MAILBOX_FILTERS.find((filter) => filter.value === mailboxFilter)?.label || "All";
  const visibleTenants = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return tenants.filter((tenant) => {
      const matchesName = !query || tenantDisplayName(tenant).toLowerCase().includes(query);
      const hasMailbox = Boolean(tenant.mailboxes?.length);
      const matchesMailbox = mailboxFilter === "all"
        || (mailboxFilter === "assigned" && hasMailbox)
        || (mailboxFilter === "unassigned" && !hasMailbox);
      return matchesName && matchesMailbox;
    });
  }, [mailboxFilter, searchQuery, tenants]);

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div>
          <p className="admin-page-eyebrow">Mailbox Manager</p>
          <h2>Tenants</h2>
        </div>
        <button className="admin-button admin-button-primary" type="button" onClick={openCreateProfile}>Add Tenant</button>
      </div>

      <div className="admin-tenant-toolbar">
        <label className="admin-tenant-search">
          <span className="admin-visually-hidden">Search tenants by name</span>
          <input
            type="search"
            placeholder="Search tenants by name"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
          />
        </label>
        <div className="admin-tenant-filter-control">
          <button className="admin-button admin-button-secondary" type="button" aria-haspopup="dialog" onClick={() => setFilterOpen(true)}>
            Filter
          </button>
          <span className="admin-filter-summary">Filter: {filterLabel}</span>
        </div>
      </div>

      {pageError && <p className="admin-empty-state admin-error-state">{pageError}</p>}
      {success && <p className="admin-empty-state admin-success-state">{success}</p>}

      {isLoading ? (
        <p className="admin-empty-state">Loading tenants...</p>
      ) : (
        <TenantTable tenants={visibleTenants} onViewFiles={openFiles} onViewProfile={openTenantProfile} />
      )}

      {filterOpen && (
        <TenantFilterModal
          value={mailboxFilter}
          onApply={(selection) => {
            setMailboxFilter(selection);
            setFilterOpen(false);
          }}
          onClose={() => setFilterOpen(false)}
        />
      )}

      {profileOpen && (
        <TenantProfileModal
          tenant={selectedTenant}
          mailboxes={mailboxes}
          hidden={Boolean(pendingUpdate || confirmDelete)}
          isSubmitting={isSaving}
          submitError={profileError}
          externalErrors={profileFieldErrors}
          filesSection={!selectedTenant && (
            <>
              {creationFileError && <p className="admin-field-error" role="alert">{creationFileError}</p>}
              <TenantCreationFiles
                files={creationFiles}
                onAdd={addCreationFiles}
                onRemove={(id) => setCreationFiles((current) => current.filter((entry) => entry.id !== id))}
                isWorking={isSaving}
              />
            </>
          )}
          completion={createdTenant && (
            <>
              <div className="admin-modal-body">
                <TenantCreationFiles files={creationFiles} isWorking={isSaving} completed />
                <p role="status" aria-live="polite">{creationFiles.filter((entry) => entry.status === "uploaded").length} of {creationFiles.length} files uploaded.</p>
              </div>
              <div className="admin-modal-footer">
                <button className="admin-button admin-button-secondary" type="button" onClick={closeProfile} disabled={isSaving}>Finish Without Remaining Files</button>
                <button className="admin-button admin-button-primary" type="button" onClick={retryCreationFiles} disabled={isSaving}>{isSaving ? "Uploading..." : "Retry Failed Uploads"}</button>
              </div>
            </>
          )}
          onSubmit={saveTenant}
          onDelete={() => {
            setDeleteError("");
            setConfirmDelete(true);
          }}
          onClose={closeProfile}
        />
      )}

      {pendingUpdate && (
        <ConfirmModal
          title="Update tenant profile?"
          description="The new information will override the existing tenant profile."
          items={pendingUpdate.changedFields.map((field) => TENANT_FIELD_LABELS[field])}
          confirmLabel="Confirm Update"
          isSubmitting={isSaving}
          onConfirm={applyTenantUpdate}
          onCancel={() => setPendingUpdate(null)}
        />
      )}

      {confirmDelete && selectedTenant && (
        <ConfirmModal
          title={`Delete ${tenantDisplayName(selectedTenant)}?`}
          description="This moves the tenant and active documents to Trash. Assigned mailboxes will be released."
          items={mailboxNumbers.length ? [`Mailbox(es) to release: ${mailboxNumbers.join(", ")}`] : []}
          confirmLabel="Delete Tenant"
          confirmTone="danger"
          isSubmitting={isSaving}
          error={deleteError}
          onConfirm={deleteTenant}
          onCancel={() => {
            setConfirmDelete(false);
            setDeleteError("");
          }}
        />
      )}

      {filesTenant && (
        <TenantFilesModal
          tenant={filesTenant}
          documents={documents}
          isLoading={filesLoading}
          isWorking={filesWorking}
          selectedFile={selectedFile}
          inputVersion={fileInputVersion}
          error={fileError}
          onFileChange={setSelectedFile}
          onUpload={uploadDocument}
          onOpenDoc={openDocument}
          onDeleteDoc={deleteDocument}
          onClose={closeFiles}
        />
      )}
    </div>
  );
}
